/**
 * The `en` resource bundles (BP-01 §9: i18n namespaces `common`, `auth`,
 * `settings`; ARCHITECTURE §3 i18n: "All UI strings via i18next keys from day
 * one; `en` only in v1").
 *
 * Every visible string in `src/apps/**` resolves to a key in one of these three
 * bundles. Hindi/Marathi/Gujarati arrive in v2 by adding sibling bundles; no
 * component changes when they do.
 */

/** Chrome shared by both apps: shells, banners, generic actions and states. */
export const common = {
  app: {
    staff: '26AD staff',
    client: '26AD',
  },
  nav: {
    label: 'Settings',
    tenant: 'Tenant',
    users: 'Users',
    security: 'Security',
    logout: 'Log out',
    signIn: 'Sign in',
    main: 'Main',
    more: 'More',
    account: 'Account',
    role: {
      owner: 'Owner',
      manager: 'Manager',
      frontdesk: 'Front desk',
      technician: 'Technician',
      accountant: 'Accountant',
    },
    // The staff sidebar (docs/design/staff-shell, N-StaffNav).
    group: {
      frontDesk: 'Front desk',
      salon: 'Salon',
      business: 'Business',
    },
    item: {
      home: 'Home',
      calendar: 'Calendar',
      myDay: 'My day',
      sales: 'Sales',
      clients: 'Clients',
      dayClose: 'Day close',
      catalogue: 'Catalogue',
      services: 'Services',
      stock: 'Stock',
      team: 'Team',
      marketing: 'Marketing',
      reports: 'Reports',
      money: 'Money',
      settings: 'Settings',
      printQueue: 'Print queue',
      myCommission: 'My commission',
      commissions: 'Commissions',
      myShifts: 'My shifts',
      accounting: 'Accounting',
    },
    // Section tab strips (S01-StaffIA).
    tab: {
      label: 'Sections',
      day: 'Day',
      waitlist: 'Waitlist',
      invoices: 'Invoices',
      newSale: 'New sale',
      awaiting: 'Awaiting confirmation',
      settlements: 'Settlements',
      callbacks: 'Provider callbacks',
      clients: 'Clients',
      dataRequests: 'Data requests',
      privacyNotice: 'Privacy notice',
      services: 'Services',
      products: 'Products',
      stations: 'Stations',
      colourBank: 'Colour bank',
      import: 'Import',
      onHand: 'On hand',
      reorder: 'Reorder',
      purchaseOrders: 'Purchase orders',
      receive: 'Receive',
      transfers: 'Transfers',
      suppliers: 'Suppliers',
      staff: 'Staff',
      roster: 'Roster',
      attendance: 'Attendance',
      hours: 'Hours',
      leave: 'Leave',
      pins: 'PINs',
      checkIn: 'Check-in',
      campaigns: 'Campaigns',
      giftCards: 'Gift cards',
      assistant: 'Assistant',
      commissions: 'Commissions',
      expenses: 'Expenses',
      accountingExports: 'Accounting exports',
      dryRun: 'Dry run',
    },
  },
  // The Settings hub (docs/design/staff-settings, six groups).
  hub: {
    title: 'Settings',
    group: {
      salon: 'Salon',
      people: 'People & access',
      money: 'Money & GST',
      messaging: 'Messaging',
      hardware: 'Hardware',
      system: 'System',
    },
    link: {
      setup: 'Menu and setup',
      salon: 'Salon details',
      outlets: 'Outlets',
      branches: 'Branches',
      bookingRules: 'Booking rules',
      bookingExtras: 'Booking extras',
      packages: 'Packages',
      users: 'Users',
      security: 'Security',
      commissionRules: 'Commission rules',
      billing: 'Billing and GST',
      paymentCallbacks: 'Payment callbacks',
      terminals: 'Card terminals',
      accounting: 'Accounting',
      expenses: 'Expense categories',
      messaging: 'Messaging',
      relay: 'Message relay',
      channels: 'Channels',
      marketing: 'Marketing',
      printers: 'Printers',
      printAgents: 'Print agents',
      printQueue: 'Print queue',
      licence: 'Licence',
      bundles: 'Bundles',
      backups: 'Backups',
      apiKeys: 'API keys',
      webhooks: 'Webhooks',
      arDataset: 'AR dataset',
      arModels: 'AR models',
      nailArt: 'Nail art',
      sampleData: 'Sample data',
    },
  },
  action: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    retry: 'Try again',
  },
  state: {
    loading: 'Loading…',
    empty: 'Nothing here yet.',
    error: 'Something went wrong.',
  },
  banner: {
    readonly:
      'This workspace is read-only. You can look at everything; changes are refused until it is reactivated.',
    offline: 'You are offline. Showing the last data this device stored.',
  },
  route: {
    notFound: 'Page not found',
    forbidden: 'You do not have access to this page.',
  },
  locale: {
    label: 'Language',
    switch: 'Change language',
    // BP-19 §14: the three drafts ship unreviewed and the switcher says so, rather
    // than letting a reader assume a salon checked the wording.
    draft: 'Hindi, Marathi and Gujarati are machine drafts the salon has not reviewed yet.',
  },
  hello: {
    title: '26AD hello',
    loading: 'Contacting the 26AD api…',
    error: 'The 26AD api could not be reached.',
  },
} as const;

/** Everything on the two `/login` screens and the TOTP step. */
export const auth = {
  login: {
    title: 'Sign in',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    redirecting: 'Taking you to the sign-in page…',
  },
  totp: {
    title: 'Two-step verification',
    hint: 'Your account is protected by an authenticator app. Enter the current six-digit code.',
    code: 'One-time code',
    submit: 'Verify',
  },
  otp: {
    title: 'Sign in',
    phone: 'Mobile number',
    phoneHint: 'In international form, e.g. +919820000000',
    send: 'Send code',
    sending: 'Sending…',
    sent: 'We sent a code to {{phone}}.',
    code: 'Six-digit code',
    verify: 'Verify',
    verifying: 'Verifying…',
    devCode: 'Development code: {{code}}',
    signedIn: 'Signed in as {{phone}}',
    role: 'Role: client',
  },
  error: {
    invalid: 'That email, password or one-time code is not right.',
    locked: 'Too many attempts. Wait a minute and try again.',
    readonly: 'This workspace is read-only, so signing in cannot start a session right now.',
    unknown: 'Sign-in failed. Try again.',
    phone: 'Enter a mobile number in international form, e.g. +919820000000.',
    otp: 'That code is not right.',
  },
} as const;

/** The three `/settings/*` screens. */
export const settings = {
  tenant: {
    title: 'Tenant',
    name: 'Name',
    timezone: 'Timezone',
    gstin: 'GSTIN',
    gstinHint: 'Leave blank if this salon is not registered.',
    saved: 'Saved.',
  },
  users: {
    title: 'Users',
    name: 'Name',
    email: 'Email',
    status: 'Status',
    roles: 'Roles',
    disable: 'Disable',
    enable: 'Enable',
    editRoles: 'Edit roles',
    rolesTitle: 'Roles for {{name}}',
    rolesSaved: 'Roles saved.',
    none: 'No users yet.',
  },
  security: {
    title: 'Security',
    totpTitle: 'Authenticator app',
    totpStart: 'Set up an authenticator',
    totpScan: 'Add this to your authenticator app, then enter the code it shows.',
    totpSecret: 'Setup key',
    totpUri: 'Setup link',
    recoveryCodes: 'Recovery codes',
    totpCode: 'Six-digit code',
    totpConfirm: 'Confirm',
    totpEnabled: 'Authenticator app enabled.',
    totpAlreadyEnabled: 'An authenticator app is already set up for this account.',
    sessionsTitle: 'Sessions',
    sessionDevice: 'Device',
    sessionExpires: 'Expires',
    sessionCurrent: 'This device',
    sessionRevoke: 'Sign out',
    sessionsEmpty: 'No sessions to show.',
    sessionsNote:
      'This browser can only see the session it is holding. A full list needs an api route BP-01 §5 does not define yet.',
  },
  role: {
    owner: 'Owner',
    manager: 'Manager',
    frontdesk: 'Front desk',
    technician: 'Technician',
    accountant: 'Accountant',
    client: 'Client',
  },
} as const;

/** The three namespaces BP-01 §9 fixes, as i18next wants them. */
export const en = { common, auth, settings } as const;

/** The namespace names, in the order §9 lists them. */
export const NAMESPACES = ['common', 'auth', 'settings'] as const;

export type Namespace = (typeof NAMESPACES)[number];
